// cesium-imagery.ts - Manages imagery providers and layers

import {
  Cesium3DTileStyle,
  Cesium3DTileset,
  CesiumTerrainProvider,
  Credit,
  createOsmBuildingsAsync,
  createWorldImageryAsync,
  createWorldTerrainAsync,
  ImageryLayer,
  IonResource,
  IonWorldImageryStyle,
  OpenStreetMapImageryProvider,
  Terrain,
  UrlTemplateImageryProvider,
} from "cesium";
import type { ImageryStyle } from "./cesium-types";

// Keep track of tilesets
let osmTileset: any = null;
let googlePhotorealisticTileset: any = null;

// Main function to change the imagery provider
export async function changeImageryProvider(
  viewer: any,
  style: ImageryStyle,
  isMobileDevice: boolean,
  clippingRectangle?: any,
) {
  if (!viewer) {
    return;
  }

  try {
    let targetStyle = style;
    if (isMobileDevice) {
      const isIPhone = /iphone/i.test(navigator.userAgent);
      if (
        isIPhone &&
        (targetStyle === "satellite-buildings" ||
          targetStyle === "photorealistic")
      ) {
        targetStyle = "satellite";
      }
    }

    const imageryLayers = viewer.imageryLayers;
    imageryLayers.removeAll();

    if (googlePhotorealisticTileset && targetStyle !== "photorealistic") {
      viewer.scene.primitives.remove(googlePhotorealisticTileset);
      googlePhotorealisticTileset = null;
    }

    const isMapTiler = targetStyle.startsWith("maptiler");

    if (!isMapTiler) {
      viewer.terrainProvider = await createWorldTerrainAsync();
    }

    // Set lighting based on style
    viewer.scene.globe.enableLighting = !isMapTiler;

    let newProvider: any;

    if (isMapTiler) {
      const key = "uj47eDVyT76qCp4tIJS0";
      const styleName = targetStyle.replace("maptiler-", "");

      const styleInfo = {
        streets: { suffix: "-v2", format: "png" },
        basic: { suffix: "-v2", format: "png" },
        bright: { suffix: "-v2", format: "png" },
        pastel: { suffix: "", format: "png" },
        topo: { suffix: "-v2", format: "png", terrain: true },
        toner: { suffix: "-v2", format: "png" },
        hybrid: { suffix: "", format: "jpg", terrain: true },
        winter: { suffix: "-v2", format: "png" },
        positron: { suffix: "", format: "png" },
      }[styleName];

      if (styleInfo) {
        const url = `https://api.maptiler.com/maps/${styleName}${styleInfo.suffix}/{z}/{x}/{y}.${styleInfo.format}?key=${key}`;
        newProvider = new UrlTemplateImageryProvider({
          url: url,
          minimumLevel: 0,
          maximumLevel: 20,
          tileWidth: 512,
          tileHeight: 512,
          credit: new Credit(
            '<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> ' +
              '<a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>',
            true,
          ),
        });

        if (styleInfo.terrain) {
          viewer.terrainProvider = await CesiumTerrainProvider.fromUrl(
            `https://api.maptiler.com/tiles/terrain-quantized-mesh-v2/?key=${key}`,
            {
              requestVertexNormals: true,
            },
          );
        } else {
          viewer.terrainProvider = await createWorldTerrainAsync();
        }
      }
    } else {
      switch (targetStyle) {
        case "openstreetmap":
          newProvider = new OpenStreetMapImageryProvider({});
          break;
        case "satellite":
          newProvider = await createWorldImageryAsync({
            style: IonWorldImageryStyle.AERIAL,
          });
          break;
        case "satellite-buildings":
          newProvider = await createWorldImageryAsync({
            style: IonWorldImageryStyle.AERIAL,
          });
          if (!isMobileDevice) {
            if (!osmTileset) {
              osmTileset = await createOsmBuildingsAsync();
              viewer.scene.primitives.add(osmTileset);
            }
          }
          break;
        case "photorealistic":
          if (isMobileDevice) {
            return changeImageryProvider(viewer, "satellite", isMobileDevice);
          }
          newProvider = await createWorldImageryAsync({
            style: IonWorldImageryStyle.AERIAL,
          });
          if (!googlePhotorealisticTileset) {
            googlePhotorealisticTileset = await Cesium3DTileset.fromUrl(
              await IonResource.fromAssetId(2275207),
            );

            viewer.scene.primitives.add(googlePhotorealisticTileset);
          }
          break;
        default:
          return;
      }
    }

    if (newProvider) {
      const layer = new ImageryLayer(newProvider, {
        rectangle: clippingRectangle,
      });
      imageryLayers.add(layer);
    }

    if (osmTileset) {
      osmTileset.show = style === "satellite-buildings";
    }

    viewer.scene.requestRender();
  } catch (error) {
    const fallbackProvider = new OpenStreetMapImageryProvider({});
    viewer.imageryLayers.addImageryProvider(fallbackProvider);
    if (osmTileset) {
      osmTileset.show = true;
    }
  }
}

export function setOsmBuildingsColor(
  viewer: any,
  color: string,
  opacity: number,
) {
  if (osmTileset) {
    setTimeout(() => {
      console.log("SET COLOR", color, opacity);
      osmTileset.style = new Cesium3DTileStyle({
        color: `color('${color}', ${opacity})`,
      });
      viewer.scene.requestRender();
    }, 3000);
  }
}

export async function toggleOsmBuildings(
  viewer: any,
  enabled: boolean,
  mapData: any,
) {
  if (enabled && !osmTileset) {
    try {
      const osmBuildingsTileset = await createOsmBuildingsAsync();
      if (osmBuildingsTileset) {
        setTimeout(() => {
          viewer.scene.primitives.add(osmBuildingsTileset);
          osmTileset = osmBuildingsTileset;
          osmTileset.style = new Cesium3DTileStyle({
            color: `color('${mapData.osmBuildingColor}', ${mapData.osmBuildingOpacity})`,
          });

          viewer.scene.requestRender();
        }, 1000);
      }
    } catch (error: any) {
      console.error("Error creating OSM buildings:", error);
    }
  } else if (!enabled && osmTileset) {
    viewer.scene.primitives.remove(osmTileset);
    osmTileset = null;
    viewer.scene.requestRender();
  }
}
